// src/pages/employee/EmployeeJobBoard.jsx
import React, { useState } from "react";
import { EMPLOYEE_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { useJobOpenings, useReferrals, createReferral, createCandidate } from "../../hooks/useRecruitment";
import { useAuth } from "../../context/AuthContext";

export default function EmployeeJobBoard() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid || userProfile?.id;
  const { data: jobOpenings, loading: jobsLoading } = useJobOpenings({ status: "Open" });
  const { data: myReferrals, loading: refLoading } = useReferrals(uid);

  const [activeTab, setActiveTab] = useState("jobs");
  const [showReferModal, setShowReferModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const [referForm, setReferForm] = useState({
    candidateName: "",
    candidateEmail: "",
    candidatePhone: "",
    linkedIn: "",
    relationship: "Former Colleague",
    notes: "",
  });

  async function handleInternalApply(job) {
    if (!window.confirm(`Confirm internal application for "${job.title}"?`)) return;
    await createCandidate({
      name: userProfile?.displayName || "Employee",
      email: userProfile?.email || "",
      phone: userProfile?.phone || "",
      appliedPosition: job.title,
      jobId: job.id,
      experience: "Internal Transfer",
      currentCompany: "Current Employee",
      source: "Internal Transfer",
      stage: "Applied",
      aiScore: 90,
      createdByName: userProfile?.displayName || "Employee",
    });
    alert("Internal application submitted successfully!");
  }

  async function handleReferSubmit(e) {
    e.preventDefault();
    if (!selectedJob) return;

    await createReferral({
      ...referForm,
      jobId: selectedJob.id,
      jobTitle: selectedJob.title,
      referredById: uid,
      referredByName: userProfile?.displayName || "Employee",
    });

    // Also create Candidate in ATS
    await createCandidate({
      name: referForm.candidateName,
      email: referForm.candidateEmail,
      phone: referForm.candidatePhone,
      appliedPosition: selectedJob.title,
      jobId: selectedJob.id,
      source: "Employee Referral",
      referrerName: userProfile?.displayName || "Employee",
      stage: "Applied",
      aiScore: 80,
      createdByName: userProfile?.displayName || "Employee",
    });

    setShowReferModal(false);
    alert("Referral submitted successfully! Candidate has been added to ATS.");
  }

  return (
    <Layout navItems={EMPLOYEE_NAV} panelLabel="My Portal">
      <style>{`
        .emp-job-tabs { display: flex; gap: 0.5rem; border-bottom: 2px solid var(--border, #e5e7eb); margin-bottom: 1.5rem; }
        .emp-job-tab { padding: 0.6rem 1.2rem; background: none; border: none; font-weight: 600; font-size: 0.86rem; cursor: pointer; color: var(--text-muted); border-bottom: 3px solid transparent; margin-bottom: -2px; }
        .emp-job-tab.active { color: #1b4b31; border-bottom-color: #1b4b31; font-weight: 700; }
        .job-post-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Internal Job Board & Referrals</h1>
          <p className="page-subtitle">Explore internal career opportunities and refer top talent to earn referral rewards</p>
        </div>
      </div>

      <div className="emp-job-tabs">
        <button className={`emp-job-tab ${activeTab === "jobs" ? "active" : ""}`} onClick={() => setActiveTab("jobs")}>
          💼 Open Internal Positions ({jobOpenings.length})
        </button>
        <button className={`emp-job-tab ${activeTab === "referrals" ? "active" : ""}`} onClick={() => setActiveTab("referrals")}>
          🤝 My Referrals ({myReferrals.length})
        </button>
      </div>

      {activeTab === "jobs" && (
        <div>
          {jobsLoading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Jobs...</div>
          ) : jobOpenings.length === 0 ? (
            <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
              No open job postings at this time.
            </div>
          ) : (
            jobOpenings.map((job) => (
              <div key={job.id} className="job-post-card">
                <div>
                  <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--text-primary)" }}>{job.title}</div>
                  <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 4 }}>
                    🏢 {job.department} · 📍 {job.location || "On-site"} · 💼 {job.employmentType} · Vacancies: <strong>{job.vacancies || 1}</strong>
                  </div>
                  {job.skillsRequired && (
                    <div style={{ display: "flex", gap: "0.4rem", marginTop: 8, flexWrap: "wrap" }}>
                      {(Array.isArray(job.skillsRequired) ? job.skillsRequired : String(job.skillsRequired).split(",")).map((sk) => (
                        <span key={sk} style={{ background: "var(--bg-secondary)", color: "var(--text-muted)", padding: "0.15rem 0.5rem", borderRadius: 4, fontSize: "0.72rem" }}>
                          {sk}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button className="btn-secondary" style={{ fontSize: "0.78rem" }} onClick={() => handleInternalApply(job)}>
                    Internal Apply
                  </button>
                  <button
                    className="btn-primary"
                    style={{ fontSize: "0.78rem" }}
                    onClick={() => {
                      setSelectedJob(job);
                      setShowReferModal(true);
                    }}
                  >
                    🤝 Refer Candidate
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "referrals" && (
        <div>
          {refLoading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Referrals...</div>
          ) : myReferrals.length === 0 ? (
            <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
              You haven't referred any candidates yet. Click "Refer Candidate" on any open job to start!
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              {myReferrals.map((ref) => (
                <div key={ref.id} className="job-post-card">
                  <div>
                    <div style={{ fontWeight: 700 }}>{ref.candidateName}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 2 }}>
                      Referred for: <strong>{ref.jobTitle}</strong> · {ref.candidateEmail}
                    </div>
                  </div>
                  <span style={{ background: "#d1fae5", color: "#065f46", padding: "0.2rem 0.65rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700 }}>
                    Status: {ref.status || "Submitted"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Refer Modal */}
      {showReferModal && selectedJob && (
        <div className="modal-overlay">
          <div className="modal-body" style={{ maxWidth: 500 }}>
            <h3 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Refer Candidate for {selectedJob.title}</h3>
            <form onSubmit={handleReferSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Candidate Full Name *</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  value={referForm.candidateName}
                  onChange={(e) => setReferForm({ ...referForm, candidateName: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Candidate Email *</label>
                <input
                  type="email"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  value={referForm.candidateEmail}
                  onChange={(e) => setReferForm({ ...referForm, candidateEmail: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Phone Number</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  value={referForm.candidatePhone}
                  onChange={(e) => setReferForm({ ...referForm, candidatePhone: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>LinkedIn Profile URL</label>
                <input
                  type="url"
                  className="ats-input"
                  style={{ width: "100%" }}
                  placeholder="https://linkedin.com/in/username"
                  value={referForm.linkedIn}
                  onChange={(e) => setReferForm({ ...referForm, linkedIn: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowReferModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Submit Referral</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
